/**
 * Chart Loading Skeleton Component
 *
 * Provides a loading fallback for lazy-loaded chart components.
 * Displays an animated skeleton that matches typical chart dimensions.
 *
 * @module components/charts/ChartSkeleton
 */

import React from 'react'

export interface ChartSkeletonProps {
  height?: string | number
  className?: string
}

/**
 * ChartSkeleton - Loading fallback for lazy-loaded charts (AC3)
 *
 * Shows an animated skeleton with chart-like structure while
 * the actual Recharts library is being loaded asynchronously.
 *
 * @param props.height - Chart height (default: 'h-96')
 * @param props.className - Optional CSS classes
 */
export const ChartSkeleton: React.FC<ChartSkeletonProps> = ({
  height = 'h-96',
  className = '',
}) => {
  return (
    <div
      className={`w-full ${typeof height === 'string' ? height : ''} ${className} animate-pulse`}
      style={typeof height === 'number' ? { height: `${height}px` } : undefined}
      role="status"
      aria-label="Loading chart..."
    >
      <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-6 h-full flex flex-col">
        {/* Chart title skeleton */}
        <div className="mb-6">
          <div className="h-6 bg-gray-300 dark:bg-gray-700 rounded w-1/3 mb-2"></div>
          <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-1/2"></div>
        </div>

        {/* Chart area skeleton */}
        <div className="flex-1 flex items-end space-x-2 mb-4">
          {/* Simulated bars/lines */}
          {[40, 55, 45, 70, 60, 80, 65, 75, 50, 85, 70, 90].map((height, index) => (
            <div
              key={index}
              className="flex-1 bg-gray-300 dark:bg-gray-700 rounded-t"
              style={{ height: `${height}%` }}
            ></div>
          ))}
        </div>

        {/* Legend skeleton */}
        <div className="flex justify-center gap-6 mt-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-gray-300 dark:bg-gray-700"></div>
            <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-20"></div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-gray-300 dark:bg-gray-700"></div>
            <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-20"></div>
          </div>
        </div>
      </div>

      {/* Screen reader text */}
      <span className="sr-only">Carregando gráfico...</span>
    </div>
  )
}

ChartSkeleton.displayName = 'ChartSkeleton'
